/**
 * useRejectionDialog Hook
 *
 * Shared hook for rejection/approval workflows across the application.
 * Handles form state, validation, and submission for rejection dialogs.
 *
 * Used in:
 * - Waste Orders (reject order)
 * - School Requests (reject registration)
 * - Lesson Answers (reject submission)
 *
 * Created: 2025-11-02 (Phase 5 - Admin Forms Refactoring)
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { rejectionSchema, type RejectionFormData } from '@/shared/validation/rejection.schemas';

/**
 * Hook Options
 */
interface UseRejectionDialogOptions {
  /**
   * Callback function when rejection is submitted
   * Receives the rejection reason string
   */
  onReject: (reason: string) => void | Promise<void>;

  /**
   * Whether the rejection operation is in progress
   * Controls loading states in the UI
   */
  loading?: boolean;

  /**
   * Callback when dialog closes/resets
   * Used to clear form state
   */
  onClose?: () => void;
}

/**
 * Hook Return Value
 */
interface UseRejectionDialogReturn {
  /**
   * React Hook Form instance
   * Pass to <Form> component
   */
  form: ReturnType<typeof useForm<RejectionFormData>>;

  /**
   * Form submission handler
   * Pass to <form onSubmit={}>
   */
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;

  /**
   * Whether the form is currently submitting
   */
  loading: boolean;

  /**
   * Reset form to default values
   * Call when dialog closes
   */
  resetForm: () => void;
}

/**
 * useRejectionDialog Hook
 *
 * Provides form state and validation for rejection dialogs.
 *
 * @example
 * ```tsx
 * function MyDialog({ onReject, loading }) {
 *   const { form, onSubmit, resetForm } = useRejectionDialog({
 *     onReject,
 *     loading,
 *   });
 *
 *   return (
 *     <Dialog onClose={resetForm}>
 *       <Form {...form}>
 *         <form onSubmit={onSubmit}>
 *           <FormField name="rejectReason" />
 *           <Button type="submit" disabled={loading}>
 *             Respinge
 *           </Button>
 *         </form>
 *       </Form>
 *     </Dialog>
 *   );
 * }
 * ```
 */
export function useRejectionDialog({
  onReject,
  loading = false,
  onClose,
}: UseRejectionDialogOptions): UseRejectionDialogReturn {
  // Initialize form with Zod validation
  const form = useForm<RejectionFormData>({
    resolver: zodResolver(rejectionSchema),
    defaultValues: {
      rejectReason: '',
    },
    mode: 'onBlur', // Validate on blur for better UX
  });

  /**
   * Handle form submission
   * Calls onReject with the rejection reason
   */
  const handleSubmit = async (data: RejectionFormData) => {
    try {
      await onReject(data.rejectReason);
      // Reset form after successful rejection
      form.reset();
      onClose?.();
    } catch (error) {
      // Error handling is done by parent component
      // (toasts, backend error mapping, etc.)
      console.error('Rejection error:', error);
    }
  };

  /**
   * Reset form to default values
   * Call when dialog closes or is cancelled
   */
  const resetForm = () => {
    form.reset({
      rejectReason: '',
    });
  };

  return {
    form,
    onSubmit: form.handleSubmit(handleSubmit),
    loading,
    resetForm,
  };
}
